import { formatMessage, isPluralRecord, selectPlural } from './format-message'
import { intlTag } from './locales'
import { getTranslation, translationsVersion } from './registry'
import type { CatalogNode, CopyModule, PluralRecord, ResolvedCopy } from './types'

/**
 * The fallback chain, in order:
 *
 * 1. the requested locale's leaf, when it exists and has the same shape;
 * 2. the English leaf — always present, statically imported, precached, which
 *    is exactly what makes it usable as a fallback (D-L6 obligation 2);
 * 3. `⟦namespace.path⟧`.
 *
 * Step 3 is unreachable through the type system — `ResolvedCopy<T>` is derived
 * from the English catalogue, so every path it offers exists there. It fires
 * only for a hand-built catalogue or a bad merge, and it renders the key rather
 * than an empty string because a blank label reads as "this record has no name"
 * and gets believed, where `⟦checks.page.title⟧` gets reported.
 */
function missing(path: string): string {
  return `⟦${path}⟧`
}

type Leaf = string | PluralRecord
type Node = Record<string, unknown>

function resolveNode(en: Node, translated: Node | undefined, locale: string, path: string): Node {
  const out: Node = {}
  for (const [name, source] of Object.entries(en)) {
    const childPath = path ? `${path}.${name}` : name
    const candidate = translated?.[name]

    if (typeof source === 'string') {
      const template = typeof candidate === 'string' ? candidate : source
      // A template with no placeholders stays a plain string so it can be
      // handed straight to `title=` / `aria-label=`; only the ones that need
      // arguments become callable. The shape is decided per leaf at build time
      // here and mirrored by `ResolvedString<S>` at the type level.
      out[name] = /\{[^{}\s]+\}/.test(template)
        ? (values?: Record<string, string | number>) => formatMessage(template, values)
        : template
      continue
    }

    if (isPluralRecord(source)) {
      // The translated record is used WHOLE, not merged category-by-category: a
      // French plural set is complete for French, and grafting an English
      // `one` into it would produce a sentence in neither language.
      const record = isPluralRecord(candidate) ? candidate : source
      const pluralTag = intlTag(locale)
      out[name] = (values: { count: number } & Record<string, string | number>) =>
        formatMessage(selectPlural(record, values.count, pluralTag), values)
      continue
    }

    if (typeof source === 'object' && source !== null) {
      const child = typeof candidate === 'object' && candidate !== null ? (candidate as Node) : undefined
      out[name] = resolveNode(source as Node, child, locale, childPath)
      continue
    }

    out[name] = missing(childPath)
  }
  return Object.freeze(out)
}

interface CacheEntry {
  readonly version: number
  readonly value: unknown
}

// Per-module so a module that is never used costs nothing, and so unmounting a
// feature does not strand entries for every locale it was ever rendered in.
const cache = new WeakMap<CopyModule, Map<string, CacheEntry>>()

/**
 * Resolve a catalogue for a locale. Returns a **stable identity** for the same
 * `(module, locale, registry version)`, which is what keeps `useCopy` from
 * invalidating every `useMemo` and `React.memo` downstream on each render.
 */
export function getCopy<T extends CatalogNode>(module: CopyModule<T>, locale: string): ResolvedCopy<T> {
  let byLocale = cache.get(module as CopyModule)
  if (!byLocale) {
    byLocale = new Map()
    cache.set(module as CopyModule, byLocale)
  }
  const version = translationsVersion()
  const hit = byLocale.get(locale)
  if (hit && hit.version === version) return hit.value as ResolvedCopy<T>

  const translated = locale === 'en' ? undefined : getTranslation(module.namespace, locale)
  const value = resolveNode(module.en as Node, translated as Node | undefined, locale, module.namespace)
  byLocale.set(locale, { version, value })
  return value as ResolvedCopy<T>
}

export type { Leaf }
